export interface CarouselConfig {
    title: string;
    displayName: string;
    platform: string;
    type: string;
    contentShape: 'rect' | 'sq';
    ctaPlaceholder: string;
    sortOrder: 'DESC' | 'ASC';
}

// List of reserved content carousel titles
export const RESERVED_CONTENT_TITLES = [
    'Latest Instagram Stories',
    'Latest TikToks',
    'Latest Instagram Reels',
    'Latest YouTube Videos',
    'Latest Podcast Episodes',
    'Latest LinkedIn Posts',
    'Latest X Posts',
    'Featured Content',
    "This Week's Content",
    'Trending Products',
    'Trending Content'
];

// List of content carousel display names
export const CONTENT_DISPLAY_NAMES = [
    'instagram story',
    'tiktok',
    'reels',
    'youtube',
    'podcast',
    'featured',
    'This Week Content',
    'Trending Products',
    'Trending Content'
];

export const getCarouselConfig = (carouselType: string): CarouselConfig => {
    switch (carouselType.toLowerCase()) {
        case 'instagram story':
            return {
                title: 'Latest Instagram Stories',
                displayName: 'instagram story',
                platform: 'instagram',
                type: 'STORY',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'tiktok':
            return {
                title: 'Latest TikToks',
                displayName: 'tiktok',
                platform: 'tiktok',
                type: 'TIKTOK',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'reels':
            return {
                title: 'Latest Instagram Reels',
                displayName: 'reels',
                platform: 'instagram',
                type: 'REELS',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'youtube':
            return {
                title: 'Latest YouTube Videos',
                displayName: 'youtube',
                platform: 'youtube',
                type: 'VIDEO',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'podcast':
            return {
                title: 'Latest Podcast Episodes',
                displayName: 'podcast',
                platform: 'podcast',
                type: 'PODCAST',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'featured':
            return {
                title: 'Featured Content',
                displayName: 'featured',
                platform: 'featured',
                type: 'FEATURE',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };

        case 'this week content':
            return {
                title: "This Week's Content",
                displayName: 'This Week Content',
                platform: 'all',
                type: 'THIS_WEEK',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            };
        case 'trending content':
            return {
                title: "Trending Content",
                displayName: 'Trending Content',
                platform: 'all',
                type: 'trending content',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            }
        case 'trending products':
            return {
                title: "Trending Products",
                displayName: 'Trending Products',
                platform: 'all',
                type: 'trending products',
                contentShape: 'rect',
                ctaPlaceholder: 'Check it out!',
                sortOrder: 'DESC'
            }

        default:
            throw new Error(`Unknown carousel type: ${carouselType}`);
    }
};

// Helper function to check if a carousel type is valid
export const isValidCarouselType = (carouselType: string): boolean => {
    const validTypes = [
        'instagram story',
        'tiktok',
        'reels',
        'youtube',
        'podcast',
        'featured',
        'this week content',
        'trending products',
        'trending content'
    ];
    return validTypes.includes(carouselType.toLowerCase());
};

// Helper function to get platform icon type
export const getCarouselPlatformType = (carouselType: string): string => {
    const config = getCarouselConfig(carouselType);
    return config.platform;
};

// Helper function to validate shop links carousel title
export const validateShopLinksTitle = (title: string, carouselType?: string): { isValid: boolean; error?: string } => {
    // If it's a trending products carousel, allow the reserved title
    if (carouselType?.toLowerCase() === 'trending products' && title === 'Trending Products') {
        return { isValid: true };
    }

    if (RESERVED_CONTENT_TITLES.includes(title)) {
        return {
            isValid: false,
            error: 'This title is reserved for content carousels. Please choose a different title.'
        };
    }
    return { isValid: true };
};

// Helper function to validate title during edit operations
export const validateTitleForEdit = (
    newTitle: string,
    component: { display_name: string; name: string }
): { isValid: boolean; error?: string } => {
    // If it's a content carousel (check by display_name), allow editing its own title
    if (CONTENT_DISPLAY_NAMES.includes(component.display_name)) {
        // Get the expected title for this content type
        const config = Object.values(RESERVED_CONTENT_TITLES).find(title => {
            const matchingConfig = Object.values(getCarouselConfig(component.display_name));
            return matchingConfig && matchingConfig.some(value => value === title);
        });

        // If trying to change to a different reserved title, prevent it
        if (RESERVED_CONTENT_TITLES.includes(newTitle) && newTitle !== config) {
            return {
                isValid: false,
                error: 'Cannot change to a different reserved content carousel title.'
            };
        }

        // Allow keeping the same title or changing to a non-reserved title
        return { isValid: true };
    }

    // For non-content carousels, don't allow using any reserved titles
    if (RESERVED_CONTENT_TITLES.includes(newTitle)) {
        return {
            isValid: false,
            error: 'This title is reserved for content carousels. Please choose a different title.'
        };
    }

    return { isValid: true };
}; 